import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import * as BenchlingUtil from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SchemasListing, {SchemasListingSchema} from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import * as Colors from 'ui-library/Colors';

export interface LocationSchemaListing extends SchemasListingSchema {
  numValidationFailures: number;
  fields: Array<SchemaField>;
}

interface Props {
  isWritable: boolean;
  locationSchemas?: Array<LocationSchemaListing> | null;
  ownerUrlId: string;
  registryId: string;
}

class LocationSchemasListing extends React.Component<Props> {
  private handleCreate = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getCreateLocationSchemaUrl(this.props.ownerUrlId, this.props.registryId)
    );
  };

  private urlForSchema = (schemaId: string): string =>
    RegistryUtil.getLocationSchemaSettingsUrl(this.props.ownerUrlId, this.props.registryId, schemaId);

  private renderValidationSummaryColumns(locationSchema: LocationSchemaListing): Array<JSX.Element> {
    if (locationSchema.numValidationFailures === 0) {
      return [<span />];
    }
    const schemaValidationSectionUrl = `${RegistryUtil.getLocationSchemaSettingsUrl(
      this.props.ownerUrlId,
      this.props.registryId,
      locationSchema.id
    )}/validation`;
    return [
      <div
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          navigateToURL(schemaValidationSectionUrl);
        }}
        className={css(styles.validationSummaryLink)}
      >
        {locationSchema.numValidationFailures}
      </div>,
    ];
  }

  private mapSchemaToRowValue = (schema: LocationSchemaListing): Array<JSX.Element | string> => [
    <span className="u-small-spacing-left">
      {schema.name}
      <ArchiveRecordLabel item={schema} />
    </span>,
    schema.fields.map((field) => field.displayName).join(', '),
    ...this.renderValidationSummaryColumns(schema),
  ];

  render(): JSX.Element {
    const columnHeaders = [
      {name: 'Schemas', width: 4},
      {name: 'Fields', width: 6},
      {name: 'Validation Failures', width: 2},
    ];
    return (
      <SchemasListing<LocationSchemaListing>
        columnHeaders={columnHeaders}
        schemas={this.props.locationSchemas}
        schemaType={SCHEMA_TYPES.LOCATION}
        navigateToCreate={this.handleCreate}
        urlForSchema={this.urlForSchema}
        archiveSchemas={ArchiveActions.bulkArchiveStorableSchemas}
        unarchiveSchemas={ArchiveActions.bulkUnarchiveStorableSchemas}
        deleteSchema={SampleSchemaActions.deleteLocationSchema}
        isWritable={this.props.isWritable}
        mapSchemaToRowValue={this.mapSchemaToRowValue}
      />
    );
  }
}

const styles = StyleSheet.create({
  validationSummaryLink: {
    display: 'inline-block',
    height: '100%',
    width: '75%',
    float: 'left',
    textAlign: 'center',
    color: Colors.BLUE_LIGHT,
    padding: '17px 0 14px',
    margin: '-17px 0 -14px',

    ':hover': {
      color: Colors.BLUE,
      textDecoration: 'underline',
    },
  },
});

export default LocationSchemasListing;
